import React, { useEffect, useState } from 'react';
import ReferEarnModal from './ReferEarnModal';
import ApplyNowModal from './ApplyNowModal';
import { getBudgetDetailsById } from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';

const JobDetailsModal = ({ isOpen, onClose, job, onApply, onRefer }) => {
    const { isAuthenticated, showSignIn } = useAuth();
    const [isReferModalOpen, setIsReferModalOpen] = useState(false);
    const [isApplyModalOpen, setIsApplyModalOpen] = useState(false);

    // Local budget details state and loader
    const providedBudgetDetails = (() => {
        if (!job) return null;
        if (job._budgetDetails) return job._budgetDetails;
        if (job.budgetDetails && typeof job.budgetDetails === 'object') return job.budgetDetails;
        if (job.details && typeof job.details === 'object') return job.details;
        if (job.budget && typeof job.budget === 'object') return job.budget;
        return null;
    })();
    const [budgetDetails, setBudgetDetails] = useState(providedBudgetDetails);
    const [budgetLoading, setBudgetLoading] = useState(false);

    // Sync local budget details whenever a new job is passed in
    // Ensures we don't carry over budgetDetails from a previous job
    useEffect(() => {
        setBudgetDetails((() => {
            if (!job) return null;
            if (job._budgetDetails) return job._budgetDetails;
            if (job.budgetDetails && typeof job.budgetDetails === 'object') return job.budgetDetails;
            if (job.details && typeof job.details === 'object') return job.details;
            if (job.budget && typeof job.budget === 'object') return job.budget;
            return null;
        })());
    }, [job]);

    // Fetch budget details once modal opens if not already present in job prop
    useEffect(() => {
        if (!isOpen || !job) return; // only fetch when modal is visible and job exists
        let isMounted = true;
        const maybeBudgetId = job?._params?.budgetId || job?.budgetId || job?.positionId || job?.jobId || job?.id;
        // Consider details provided if job carries any rich details or has description fields already
        const hasProvided = Boolean(providedBudgetDetails);
        const hasCoreDesc = Boolean(job && (job.jobDescription || job.jobDetails || job.description));
        const hasNullJobDetails = job && (job.jobDetails === null || job.jobDetails === undefined);
        
        // Call API if: no provided details AND (no core description OR jobDetails is null) AND no budget details AND has ID
        if (!hasProvided && (!hasCoreDesc || hasNullJobDetails) && !budgetDetails && maybeBudgetId) {
            console.log('[JobDetailsModal] Fetching budget details for ID:', maybeBudgetId, 'Reason: hasNullJobDetails:', hasNullJobDetails, 'hasCoreDesc:', hasCoreDesc);
            setBudgetLoading(true);
            
            // Add a small delay to ensure modal is fully rendered before API call
            setTimeout(async () => {
                if (!isMounted) return;
                
                try {
                    const resp = await getBudgetDetailsById(maybeBudgetId);
                    if (isMounted) {
                        if (resp?.success && resp?.data) {
                            console.log('[JobDetailsModal] Budget details loaded successfully:', resp.data);
                            setBudgetDetails(resp.data);
                        } else {
                            console.warn('[JobDetailsModal] Budget details API returned no data:', resp);
                        }
                    }
                } catch (error) {
                    console.error('[JobDetailsModal] Error fetching budget details:', error);
                } finally {
                    if (isMounted) {
                        setBudgetLoading(false);
                    }
                }
            }, 100); // 100ms delay to ensure modal is rendered
        } else {
            console.log('[JobDetailsModal] Skip budget fetch. provided:', hasProvided, 'hasCoreDesc:', hasCoreDesc, 'hasNullJobDetails:', hasNullJobDetails, 'state:', Boolean(budgetDetails), 'id:', maybeBudgetId);
            setBudgetLoading(false); // Ensure loading state is cleared
        }
        
        return () => { isMounted = false; };
    }, [isOpen, job, budgetDetails, providedBudgetDetails]);

    // Normalize helpers for budget-driven fields
    const budget = budgetDetails || providedBudgetDetails || null;

    // Render-time mirror of fetch decision to avoid flashing empty state before loader
    const maybeBudgetId = job?._params?.budgetId || job?.budgetId || job?.positionId || job?.jobId || job?.id;
    const hasProvided = Boolean(providedBudgetDetails);
    const hasCoreDesc = Boolean(job && (job.jobDescription || job.jobDetails || job.description));
    const hasNullJobDetails = job && (job.jobDetails === null || job.jobDetails === undefined);
    const willFetchBudget = !hasProvided && (!hasCoreDesc || hasNullJobDetails) && !budgetDetails && !!maybeBudgetId;

    // Robust skills processor consistent with JobCard.js
    const processSkills = (jobLike, budgetLike) => {
        const rawCandidates = [];

        const pushVal = (val) => { if (val !== undefined && val !== null) rawCandidates.push(val); };

        // Common fields from job
        if (jobLike) {
            pushVal(jobLike.skills);
            pushVal(jobLike.skillsRequired);
            pushVal(jobLike.skillTags);
            pushVal(jobLike.tags);
            pushVal(jobLike.jobSkills);
            pushVal(jobLike.skill_list);
            pushVal(jobLike.tag_list);
        }

        // Common fields from budget/details
        if (budgetLike) {
            pushVal(budgetLike.skills);
            pushVal(budgetLike.skillTags);
            pushVal(budgetLike.tags);
        }

        const tokens = [];
        const addToken = (t) => { if (t && typeof t === 'string') tokens.push(t.trim()); };

        for (const c of rawCandidates) {
            if (!c) continue;
            if (Array.isArray(c)) {
                for (const item of c) {
                    if (item && typeof item === 'object') {
                        addToken(item.name || item.skillName || item.title || item.label || '');
                    } else if (typeof item === 'string' || typeof item === 'number') {
                        // Split composite strings inside arrays too
                        String(item)
                            .split(/[\n,|/;]+/)
                            .map(s => s.trim())
                            .filter(Boolean)
                            .forEach(addToken);
                    }
                }
            } else if (typeof c === 'object') {
                addToken(c.name || c.skillName || c.title || c.label || '');
            } else if (typeof c === 'string' || typeof c === 'number') {
                String(c)
                    .split(/[\n,|/;]+/)
                    .map(s => s.trim())
                    .filter(Boolean)
                    .forEach(addToken);
            }
        }

        const placeholders = new Set([
            'general','others','other','other(s)','none','n/a','na','not applicable','-','nil','null','undefined',''
        ]);

        const cleaned = tokens
            .map(t => t.replace(/^[#@\-•\s]+|[\s#@\-•]+$/g, ''))
            .filter(Boolean)
            .filter(t => !placeholders.has(t.toLowerCase()))
            .map(t => {
                // Normalize casing: Title Case common behavior
                return t.split(' ').map(w => w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : '').join(' ');
            });

        // Deduplicate while preserving order
        const seen = new Set();
        const unique = [];
        for (const s of cleaned) { if (!seen.has(s)) { seen.add(s); unique.push(s); } }

        return unique;
    };
    const parseDescriptionToList = (desc) => {
        if (!desc || typeof desc !== 'string') return [];
        // Split on common bullet separators, newlines, numbers, asterisks, etc.
        // IMPORTANT: do NOT split on a bare hyphen as it breaks words like "problem-solving".
        // Keep only the start-of-line hyphen bullet pattern (^-\s+).
        const parts = desc
            .split(/\r?\n|•|\u2022|\u25CF|\u25E6|\u2043|\*|^-\s+|\d+\.|\d+\)|\u2023|\u204C|\u204D/gm)
            .map(p => p.trim())
            .filter(p => p && p.length > 1);
        if (parts.length) return parts;
        return desc.split(/\.(?:\s+|$)/).map(s => s.trim()).filter(Boolean);
    };

    // Strict dot-based sentence splitter for Requirements and Responsibilities
    const parseByDotList = (desc) => {
        if (!desc || typeof desc !== 'string') return [];
        return desc
            .split(/\.(?:\s+|$)/)
            .map(s => s.trim())
            .filter(Boolean);
    };

    // Split a monolithic description into three parts using common section headers if present
    const splitJobDescription = (raw) => {
        const result = { descText: '', requirements: [], responsibilities: [], customSections: [] };
        if (!raw || typeof raw !== 'string') return result;
        const text = raw.replace(/\r\n/g, '\n');

        // First, detect custom headers wrapped in asterisks (e.g., *free service* or *free service*:)
        // Match asterisk-wrapped text with optional colon (inside or outside asterisks)
        const customHeaderRegex = /\*([^*]+)\*\s*:?/g;
        const customHeaders = [];
        let match;
        while ((match = customHeaderRegex.exec(text)) !== null) {
            // Remove any trailing colon from the header text
            const headerText = match[1].trim().replace(/:+$/, '');
            customHeaders.push({
                header: headerText,
                idx: match.index,
                endIdx: match.index + match[0].length, // This includes the colon if present
                fullMatch: match[0]
            });
        }

        // Broader header patterns (case-insensitive), allow variants and ampersands
        const patterns = {
            desc: [
                'job description', 'role overview', 'about the role', 'about role', 'about the job', 'overview'
            ],
            req: [
                'requirements', 'requirement', 'eligibility', 'eligibility criteria', 'what we look for', 'qualification', 'qualifications', 'skills & qualifications', 'skills and qualifications', 'key requirements', 'job requirements', 'required'
            ],
            resp: [
                'key responsibilities', 'responsibilities', 'roles & responsibilities', 'roles and responsibilities', 'what you will do', 'duties', 'job responsibilities', 'responsibility'
            ]
        };

        // Find first occurrence of each header at line start with REQUIRED colon/separator
        const findFirstHeader = (body, pats) => {
            let best = null;
            for (const p of pats) {
                // Anchor to line start and REQUIRE a colon or separator (not optional)
                const re = new RegExp(`(?:^|\\n)\\s*${p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*[:\u2013\u2014-]`, 'im');
                const m = re.exec(body);
                if (m) {
                    // Skip the newline/whitespace prefix to get actual header position
                    const matchText = m[0];
                    const prefixMatch = matchText.match(/^[\n\s]*/);
                    const prefixLen = prefixMatch ? prefixMatch[0].length : 0;
                    const idx = m.index + prefixLen;
                    const endIdx = m.index + m[0].length;
                    if (!best || idx < best.idx) best = { idx, endIdx };
                }
            }
            return best; // {idx, endIdx} or null
        };

        const reqPos = findFirstHeader(text, patterns.req);
        const respPos = findFirstHeader(text, patterns.resp);
        const descPos = findFirstHeader(text, patterns.desc);

        // Collect all header positions (standard + custom)
        const allHeaders = [
            reqPos && { ...reqPos, type: 'req' },
            respPos && { ...respPos, type: 'resp' },
            descPos && { ...descPos, type: 'desc' },
            ...customHeaders.map(ch => ({ ...ch, type: 'custom' }))
        ].filter(Boolean).sort((a, b) => a.idx - b.idx);

        // Compute overview start/end
        const firstSectionIdx = allHeaders.length > 0 ? allHeaders[0].idx : text.length;

        let overview = '';
        if (descPos) {
            // Take content after desc header until first of other sections
            const nextHeaderIdx = allHeaders.find(h => h.idx > descPos.endIdx)?.idx || text.length;
            overview = text.slice(descPos.endIdx, nextHeaderIdx).trim();
        } else {
            overview = text.slice(0, firstSectionIdx).trim();
        }

        // Extract req and resp segments accurately using their header end to next header start
        const segments = {};
        if (reqPos) {
            const nextHeaderIdx = allHeaders.find(h => h.idx > reqPos.endIdx)?.idx || text.length;
            segments.req = text.slice(reqPos.endIdx, nextHeaderIdx).trim();
        }
        if (respPos) {
            const nextHeaderIdx = allHeaders.find(h => h.idx > respPos.endIdx)?.idx || text.length;
            segments.resp = text.slice(respPos.endIdx, nextHeaderIdx).trim();
        }

        // Extract custom sections
        customHeaders.forEach(customHeader => {
            const nextHeaderIdx = allHeaders.find(h => h.idx > customHeader.endIdx)?.idx || text.length;
            const content = text.slice(customHeader.endIdx, nextHeaderIdx).trim();
            result.customSections.push({
                header: customHeader.header,
                content: parseDescriptionToList(content)
            });
        });

        // Assign result
        result.descText = overview;
        result.requirements = parseDescriptionToList(segments.req || '');
        result.responsibilities = parseDescriptionToList(segments.resp || '');

        // If neither section was found, fallback to previous paragraph-based split
        if (!reqPos && !respPos && customHeaders.length === 0) {
            const lines = text.split(/\n+/).map(s => s.trim()).filter(Boolean);
            if (lines.length) {
                const n = lines.length;
                const one = Math.max(1, Math.floor(n / 3));
                const two = Math.max(1, Math.floor((n - one) / 2));
                result.descText = lines.slice(0, one).join('\n');
                result.requirements = parseDescriptionToList(lines.slice(one, one + two).join('\n'));
                result.responsibilities = parseDescriptionToList(lines.slice(one + two).join('\n'));
            }
        }

        return result;
    };

    // As a final guard, remove any content from the first header (requirements/responsibilities/etc.) onward
    const trimAfterFirstHeader = (raw) => {
        if (!raw || typeof raw !== 'string') return '';
        const patterns = ['requirements', 'eligibility', 'qualification', 'key responsibilities', 'responsibilities', 'what you will do', 'duties'];
        const headerFinder = new RegExp(`(^|\n)\\s*(${patterns.map(p => p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})\\s*:`, 'i');
        const m = headerFinder.exec(raw);
        
        // Also check for custom headers wrapped in asterisks (with optional colon)
        const customHeaderFinder = /\*([^*]+)\*\s*:?/;
        const customMatch = customHeaderFinder.exec(raw);
        
        // Use whichever comes first
        if (m && customMatch) {
            const firstIdx = Math.min(m.index, customMatch.index);
            return raw.slice(0, firstIdx).trim();
        } else if (m) {
            return raw.slice(0, m.index).trim();
        } else if (customMatch) {
            return raw.slice(0, customMatch.index).trim();
        }
        
        return raw.trim();
    };

    // Guard render after hooks to satisfy rules-of-hooks
    if (!isOpen || !job) return null;

    // Job data processing to match Channelplay_Jobs_Website 2 reference design
    const jobData = {
        // Basic job information
        id: job.id || job.jobId || job.positionId || null,
        positionId: job.positionId || job.id || job.jobId || null,
        recruiterId: job.recruiterId || job.recruiter_id || job._params?.recruiterId || null,
        title: job.jobName || job.title || (budget?.budgetName) || 'Job Title',
        company: job.companyName || job.company || 'Company Name',
        location: (() => {
            const city = job.cityName || job.city || job.location || 'Location';
            const pin = job.pincode || job.zipCode || job.postalCode;
            return pin ? `${city} - ${pin}` : city;
        })(),
        cityName: job.cityName || job.city || job.location || 'City Name',
        pincode: job.pincode || job.zipCode || job.postalCode || null,
        salary: (() => {
            // If the job comes from the getActualUrl flow, use the salary parameter directly.
            if (job._fromActualUrl && job.salary) {
                return job.salary;
            }

            // Otherwise, for jobs from the homepage list, use the min/max CTC calculation.
            const minSalary = job.minimumCtc || '';
            const maxSalary = job.maximumCtc || '';
            const isSingleOffer = job.isSingleOfferCtc === true; // Check if CTC is based on single offer
            
            if (isSingleOffer && maxSalary) {
                // Single offer - show "Approx Offered Amount"
                const monthlyAmount = Math.round(Number(maxSalary) / 12);
                return `Approx ₹${monthlyAmount.toLocaleString()} per month`;
            } else if (minSalary && maxSalary && minSalary !== maxSalary) {
                const monthlyMin = Math.round(Number(minSalary) / 12);
                const monthlyMax = Math.round(Number(maxSalary) / 12);
                return `${monthlyMin.toLocaleString()} - ${monthlyMax.toLocaleString()} per month`;
            } else if (maxSalary) {
                const monthlyMax = Math.round(Number(maxSalary) / 12);
                return `Up to ${monthlyMax.toLocaleString()} per month`;
            } else if (minSalary) {
                const monthlyMin = Math.round(Number(minSalary) / 12);
                return `From ${monthlyMin.toLocaleString()} per month`;
            }

            // Fallback for any other case.
            return job.salary || 'Salary Not Disclosed';
        })(),
        budget: job.budget || job.maxSalaryLimit || job.salary || null,
        minSalary: job.minSalaryLimit || job.minSalary || null,
        maxSalary: job.maxSalaryLimit || job.maxSalary || null,
        type: job.jobType || 'Full-time',
        experience: job.experienceRequired || job.experience || 'Experience not specified',
        
        // Detailed job information
        description: (() => {
            const source = budget?.jobDescription || job.jobDescription || job.jobDetails || job.description;
            const split = splitJobDescription(source);
            const text = split.descText || trimAfterFirstHeader(source);
            return text || 'No description available';
        })(),
        jobDetails: (() => {
            const source = budget?.jobDescription || job.jobDescription || job.jobDetails || job.description;
            const split = splitJobDescription(source);
            const text = split.descText || trimAfterFirstHeader(source);
            return text || 'No job details available';
        })(),
        
        // Arrays for requirements, responsibilities, and skills
        requirements: (() => {
            const source = budget?.jobDescription || job.jobDescription || job.jobDetails || job.description;
            const split = splitJobDescription(source);
            const byDot = parseByDotList(split.req || split.requirements?.join('. ') || '');
            if (byDot.length) return byDot;
            const fromSplit = split.requirements || [];
            if (fromSplit.length) return fromSplit;
            const fromDesc = parseByDotList(source);
            if (fromDesc.length) return fromDesc;
            if (Array.isArray(job.requirements)) return job.requirements;
            return [];
        })(),
        responsibilities: (() => {
            const source = budget?.jobDescription || job.jobDescription || job.jobDetails || job.description;
            const split = splitJobDescription(source);
            const byDot = parseByDotList(split.resp || split.responsibilities?.join('. ') || '');
            if (byDot.length) return byDot;
            const fromSplit = split.responsibilities || [];
            if (fromSplit.length) return fromSplit;
            const fromDesc = parseByDotList(source);
            if (fromDesc.length) return fromDesc;
            if (Array.isArray(job.responsibilities)) return job.responsibilities;
            return [];
        })(),
        skills: (() => {
            const computed = processSkills(job, budget);
            return Array.isArray(computed) ? computed : [];
        })(),
        customSections: (() => {
            const source = budget?.jobDescription || job.jobDescription || job.jobDetails || job.description;
            const split = splitJobDescription(source);
            return split.customSections || [];
        })(),
        
        // Additional job metadata
        department: job.department || null,
        category: job.category || job.jobCategory || null,
        employmentType: job.employmentType || job.jobType || 'Full-time',
        workMode: job.workMode || job.remoteWork || null,
        urgency: job.urgency || job.isUrgent || false,
        postedDate: job.postedDate || job.createdAt || null,
        applicationDeadline: job.applicationDeadline || job.deadline || null,
        contactEmail: job.contactEmail || job.hrEmail || null,
        contactPhone: job.contactPhone || job.hrPhone || null,
        companyWebsite: job.companyWebsite || job.website || null,
        
        // Original job object for any additional fields
        originalJob: job,
        
        tags: [] // Removed tags as per requirements as per reference design
    };

    const handleApplyNow = () => {
        if (!isAuthenticated) {
            showSignIn();
            return;
        }
        console.log('Apply Now clicked for position ID:', jobData.positionId);
        setIsApplyModalOpen(true);
    };

    const handleReferSomeone = () => {
        if (!isAuthenticated) {
            showSignIn();
            return;
        }
        console.log('Refer Someone clicked for position ID:', jobData.positionId);
        setIsReferModalOpen(true);
    };

    return (
        <>
            {/* Job Details Modal - Channelplay_Jobs_Website 3 Reference Design */}
            <div 
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 9999,
                    padding: '1rem'
                }}
                onClick={onClose}
            >
                <div 
                    style={{
                        background: 'white',
                        borderRadius: '12px',
                        width: '100%',
                        maxWidth: '800px',
                        maxHeight: '90vh',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)'
                    }}
                    onClick={e => e.stopPropagation()}
                >
                    {/* Modal Header with Blue Gradient */}
                    <div style={{
                        position: 'sticky',
                        top: 0,
                        background: 'linear-gradient(135deg, #2563eb, #3b82f6)',
                        color: 'white',
                        padding: '24px',
                        borderRadius: '0.75rem 0.75rem 0 0',
                        zIndex: 10,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }}>
                        <div style={{ position: 'relative' }}>
                            <button
                                type="button"
                                onClick={onClose}
                                style={{
                                    position: 'absolute',
                                    top: '0',
                                    right: '0',
                                    background: 'none',
                                    border: 'none',
                                    fontSize: '1.5rem',
                                    cursor: 'pointer',
                                    color: 'rgba(255,255,255,0.8)',
                                    padding: '0.5rem',
                                    borderRadius: '50%',
                                    transition: 'all 0.2s ease',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '40px',
                                    height: '40px',
                                    zIndex: 10
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = 'rgba(255,255,255,0.1)';
                                    e.target.style.color = 'white';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = 'transparent';
                                    e.target.style.color = 'rgba(255,255,255,0.8)';
                                }}
                            >
                                <i className="fas fa-times"></i>
                            </button>
                            <div>
                                <h2 style={{ margin: '0 0 0.5rem 0', fontSize: '1.5rem', fontWeight: '700' }}>
                                    {jobData.title}
                                </h2>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', marginTop: '1rem' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.55rem', opacity: 0.9 }}>
                                        <i className="fas fa-map-marker-alt" style={{ width: '20px', textAlign: 'center' }}></i>
                                        <span style={{ fontSize: '0.875rem' }}>{jobData.location}</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', opacity: 0.9 }}>
                                        <i className="fas fa-indian-rupee-sign" style={{ width: '20px', textAlign: 'center' }}></i>
                                        <span style={{ fontSize: '0.875rem' }}>{jobData.salary}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Modal Content */}
                    <div style={{
                        flex: 1,
                        overflowY: 'auto',
                        padding: '1.5rem',
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column'
                    }}>

                        {/* Inline loader while fetching budget details */}
                        {budgetLoading && (
                            <div style={{
                                position: 'absolute',
                                inset: 0,
                                background: 'rgba(255,255,255,0.7)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 20
                            }}>
                                <div style={{ textAlign: 'center', color: '#2563eb' }}>
                                    <i className="fas fa-spinner fa-spin" style={{ fontSize: '1.5rem' }}></i>
                                    <div style={{ marginTop: '0.5rem', fontWeight: 600 }}>Loading details…</div>
                                </div>
                            </div>
                        )}
                        {/* Job Description */}
                        <div style={{ marginBottom: '1.5rem' }}>
                            <h3 style={{
                                margin: '0 0 0.75rem 0',
                                color: '#1f2937',
                                fontSize: '1.25rem',
                                fontWeight: '600',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                            }}>
                                <i className="fas fa-file-text" style={{ color: '#2563eb' }}></i>
                                Job Description
                            </h3>
                            <div style={{ color: '#6b7280', lineHeight: 1.6, fontSize: '0.875rem' }}>
                                {jobData.description}
                            </div>
                        </div>

                        {/* Requirements (derived from jobDescription when provided) */}
                        <div style={{ marginBottom: '1.5rem' }}>
                            <h3 style={{
                                margin: '0 0 0.75rem 0',
                                color: '#1f2937',
                                fontSize: '1.25rem',
                                fontWeight: '600',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                            }}>
                                <i className="fas fa-check-circle" style={{ color: '#2563eb' }}></i>
                                Requirements
                            </h3>
                            <ul style={{ color: '#6b7280', lineHeight: 1.6, fontSize: '0.875rem', paddingLeft: '0', listStyle: 'none' }}>
                                {jobData.requirements.map((req, index) => (
                                    <li key={index} style={{ marginBottom: '0.5rem', position: 'relative', paddingLeft: '1rem' }}>
                                        <span style={{ position: 'absolute', left: '0', color: '#2563eb' }}>•</span>
                                        {req}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Responsibilities (derived from jobDescription when provided) */}
                        <div style={{ marginBottom: '1.5rem' }}>
                            <h3 style={{
                                margin: '0 0 0.75rem 0',
                                color: '#1f2937',
                                fontSize: '1.25rem',
                                fontWeight: '600',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                            }}>
                                <i className="fas fa-tasks" style={{ color: '#2563eb' }}></i>
                                Key Responsibilities
                            </h3>
                            <ul style={{ color: '#6b7280', lineHeight: 1.6, fontSize: '0.875rem', paddingLeft: '0', listStyle: 'none' }}>
                                {jobData.responsibilities.map((resp, index) => (
                                    <li key={index} style={{ marginBottom: '0.5rem', position: 'relative', paddingLeft: '1rem' }}>
                                        <span style={{ position: 'absolute', left: '0', color: '#2563eb' }}>•</span>
                                        {resp}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Skills Required */}
                        <div style={{ marginBottom: '2rem' }}>
                            <h3 style={{
                                margin: '0 0 1rem 0',
                                color: '#1f2937',
                                fontSize: '1.25rem',
                                fontWeight: '600',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                            }}>
                                <i className="fas fa-lightbulb" style={{ color: '#2563eb' }}></i>
                                Skills Required
                            </h3>
                            {budgetLoading || willFetchBudget ? (
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    color: '#2563eb'
                                }}>
                                    <i className="fas fa-spinner fa-spin"></i>
                                    <span style={{ fontWeight: 600 }}>Loading skills…</span>
                                </div>
                            ) : (
                                <>
                                    {Array.isArray(jobData.skills) && jobData.skills.length > 0 ? (
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                            {jobData.skills.map((skill, index) => (
                                                <span key={index} style={{
                                                    background: '#eff6ff',
                                                    color: '#2563eb',
                                                    padding: '0.5rem 1rem',
                                                    borderRadius: '12px',
                                                    fontSize: '0.875rem',
                                                    fontWeight: '500',
                                                    border: '1px solid #dbeafe'
                                                }}>
                                                    {skill}
                                                </span>
                                            ))}
                                        </div>
                                    ) : (
                                        <div style={{ color: '#6b7280', fontSize: '0.9rem' }}>No skills listed</div>
                                    )}
                                </>
                            )}
                        </div>

                        {/* Custom Sections (from asterisk-wrapped headers like *free service*) */}
                        {jobData.customSections && jobData.customSections.length > 0 && jobData.customSections.map((section, sectionIndex) => (
                            <div key={sectionIndex} style={{ marginBottom: '1.5rem' }}>
                                <h3 style={{
                                    margin: '0 0 0.75rem 0',
                                    color: '#1f2937',
                                    fontSize: '1.25rem',
                                    fontWeight: '600',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem'
                                }}>
                                    <i className="fas fa-star" style={{ color: '#2563eb' }}></i>
                                    {section.header}
                                </h3>
                                <ul style={{ color: '#6b7280', lineHeight: 1.6, fontSize: '0.875rem', paddingLeft: '0', listStyle: 'none' }}>
                                    {section.content.map((item, itemIndex) => (
                                        <li key={itemIndex} style={{ marginBottom: '0.5rem', position: 'relative', paddingLeft: '1rem' }}>
                                            <span style={{ position: 'absolute', left: '0', color: '#2563eb' }}>•</span>
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}

                    </div>

                    {/* Fixed Action Buttons with Separator */}
                    <div style={{
                        borderTop: '1px solid #e5e7eb',
                        padding: '16px 24px',
                        backgroundColor: '#ffffff',
                        flexShrink: 0,
                        display: 'flex',
                        gap: '1rem',
                        justifyContent: 'flex-end'
                    }}>
                        <button 
                            onClick={handleReferSomeone}
                            style={{
                                background: '#f8fafc',
                                color: '#2563eb',
                                border: '2px solid #e0e7ff',
                                padding: '0.75rem 1.5rem',
                                borderRadius: '12px',
                                cursor: 'pointer',
                                fontWeight: '600',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                fontSize: '0.875rem',
                                transition: 'all 0.3s ease'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#e0e7ff';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.backgroundColor = '#f8fafc';
                            }}
                        >
                            <i className="fas fa-user-plus"></i>
                            Refer Someone
                        </button>
                        <button 
                            onClick={handleApplyNow}
                            style={{
                                background: 'linear-gradient(135deg, #2563eb, #3b82f6)',
                                color: 'white',
                                border: 'none',
                                padding: '0.75rem 2rem',
                                borderRadius: '12px',
                                cursor: 'pointer',
                                fontWeight: '600',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                fontSize: '0.875rem',
                                transition: 'all 0.3s ease'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.background = 'linear-gradient(135deg, #1d4ed8, #2563eb)';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.background = 'linear-gradient(135deg, #2563eb, #3b82f6)';
                            }}
                        >
                            <i className="fas fa-paper-plane"></i>
                            Apply Now
                        </button>
                    </div>
                </div>
            </div>
            
            {/* Refer & Earn Modal */}
            <ReferEarnModal 
                isOpen={isReferModalOpen}
                onClose={() => setIsReferModalOpen(false)}
                jobTitle={jobData.title}
                positionId={jobData.positionId}
                job={jobData}
                onReferralSuccess={() => {
                    // Close Refer modal and also close Job Details modal
                    setIsReferModalOpen(false);
                    if (typeof onClose === 'function') onClose();
                }}
            />
            
            {/* Apply Now Modal */}
            <ApplyNowModal 
                isOpen={isApplyModalOpen}
                onClose={() => setIsApplyModalOpen(false)}
                jobTitle={jobData.title}
                positionId={jobData.positionId}
                job={jobData}
                onSuccess={() => {
                    // Close Apply modal and also close Job Details modal
                    setIsApplyModalOpen(false);
                    if (typeof onClose === 'function') onClose();
                }}
            />
        </>
    );
};

export default JobDetailsModal;
